import type { SortingState } from '@tanstack/react-table'

/** Moet gelijk blijven aan LIST_MAX_PAGE_SIZE in apps/backend/src/lib/list-query.ts.
 *  De procedure accepteert méér (LIST_MAX_LIMIT, 500) voor keuzelijsten die zich
 *  met één verzoek vullen; de UI pagineert nooit grover dan dit. */
export const DEFAULT_PAGE_SIZE = 200
export const PAGE_SIZES = [25, 50, 100, 200] as const

export interface ListInput {
  q: string | undefined
  limit: number
  offset: number
  sort: { column: string; dir: 'asc' | 'desc' } | undefined
}

export function buildListInput(state: {
  search: string
  sorting: SortingState
  pageIndex: number
  pageSize: number
}): ListInput {
  const first = state.sorting[0]
  return {
    q: state.search.trim() || undefined,
    limit: state.pageSize,
    offset: state.pageIndex * state.pageSize,
    sort: first ? { column: first.id, dir: first.desc ? 'desc' : 'asc' } : undefined,
  }
}

/**
 * De gevonden set die de recordnavigatie moet volgen, afgeleid uit de invoer
 * waarmee de lijst is opgehaald: dezelfde zoekterm (gedebounced en getrimd) en
 * dezelfde sortering — en bewust niet de paginering, want vorige/volgende loopt
 * over de héle set en niet over de zichtbare pagina.
 *
 * Uit `input` en niet uit de rauwe zoekstaat, want dat is precies waar de lijst
 * en de navigatie uit elkaar kunnen lopen: wie de term publiceert die de
 * gebruiker net intikte, laat de buren een set volgen die nooit op het scherm
 * heeft gestaan.
 */
export function foundSetOf(input: ListInput): {
  search: string | undefined
  sort: ListInput['sort']
} {
  return { search: input.q, sort: input.sort }
}

/** De pagina waarop het record met deze 1-gebaseerde positie staat. */
export function pageIndexFor(position: number, pageSize: number): number {
  return Math.max(0, Math.floor((position - 1) / pageSize))
}

/**
 * Klem een pagina-index op het werkelijke totaal, zodat niemand — een
 * verouderde recordnavigatie-sprong, een filter die het resultaat laat
 * krimpen, een record dat inmiddels is verwijderd — ooit op een pagina
 * buiten bereik kan blijven staan (lege tabel, "Pagina 5/2").
 *
 * Onbekend totaal (nog aan het laden): GEEN klem — anders veert elke
 * herlaadbeurt de gebruiker terug naar pagina 1 terwijl de telling nog
 * onderweg is, wat erger is dan de bug die dit oplost. Een totaal van 0
 * klemt op pagina 0 zonder te delen.
 *
 * Puur en zonder eigen state: dit leest alleen `pageIndex` uit, het schrijft
 * 'm nooit terug. Een expliciete reset elders (bv. `goToPosition(1)` bij het
 * wisselen van referentielijst) zet gewoon de onderliggende pageIndex; deze
 * klem kan daar dus nooit "tegenin vechten" — er is niets om mee te botsen.
 */
export function clampPageIndex(
  pageIndex: number,
  pageSize: number,
  total: number | undefined,
): number {
  const raw = Math.max(0, pageIndex)
  if (total == null) return raw
  const lastPageIndex = Math.max(0, Math.ceil(total / pageSize) - 1)
  return Math.min(raw, lastPageIndex)
}

/** Waar moet een lijst na terugkeer van een record naartoe springen? De
 *  bekende positie — tenzij die onbekend is, of het record toevallig al op
 *  de geladen pagina staat, want dan hoeft er niets te gebeuren. */
export function positionJumpTarget(
  lastPosition: number | null,
  isOnLoadedPage: boolean,
): number | null {
  return lastPosition != null && !isOnLoadedPage ? lastPosition : null
}

/** Zijn twee lijstsorteringen gelijk? "Geen sortering" telt alleen als gelijk
 *  aan een andere "geen sortering" — `undefined` (ListInput.sort) en `null`
 *  (useLastOpened.lastSort, dat net als lastId/lastPosition null gebruikt
 *  voor "onbekend") tellen daarin allebei mee. */
export function sameSort(
  a: { column: string; dir: 'asc' | 'desc' } | null | undefined,
  b: { column: string; dir: 'asc' | 'desc' } | null | undefined,
): boolean {
  if (a == null || b == null) return a == null && b == null
  return a.column === b.column && a.dir === b.dir
}

const storageKey = (entityKey: string) => `list-page-size:${entityKey}`

export function readPageSize(entityKey: string): number {
  if (typeof localStorage === 'undefined') return DEFAULT_PAGE_SIZE
  const raw = Number(localStorage.getItem(storageKey(entityKey)))
  return (PAGE_SIZES as readonly number[]).includes(raw) ? raw : DEFAULT_PAGE_SIZE
}

export function writePageSize(entityKey: string, size: number): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(storageKey(entityKey), String(size))
}
